/**
 * ReferenceDataCache - In-memory cache for reference data APIs
 *
 * Purpose:
 * Caches reference data (users, statuses, priorities, groups) that rarely changes
 * during a user session. This eliminates redundant API calls when the RecordProcessPanel
 * is reopened or when users navigate between records.
 *
 * Cache Behavior:
 * - Data is cached per controlTestId (different control tests have different reference data)
 * - Cache persists for the lifetime of the page session
 * - Page refresh clears the cache (expected behavior - starts fresh session)
 * - Explicit invalidation is available if reference data is modified
 *
 * Performance Impact:
 * - First panel open: Normal API calls (~1-2.5 seconds for all reference data)
 * - Subsequent panel opens: Instant (0ms - cached data)
 * - Panel reopen after close: Instant (0ms - cached data)
 *
 * Related Ticket: GRCPRD-11761
 *
 * @example
 * import referenceDataCache from './referenceDataCache';
 *
 * // Check cache before API call
 * const cached = referenceDataCache.get(controlTestId, 'assignees');
 * if (cached) {
 *   return cached;
 * }
 *
 * // After successful API call, cache the response
 * const response = await Services.GetUsers(controlTestId);
 * referenceDataCache.set(controlTestId, 'assignees', response);
 */

const CACHE_DATA_TYPES = ['assignees', 'statuses', 'priorities', 'groups'];

class ReferenceDataCache {
  constructor() {
    this._cache = new Map();
  }

  /**
   * Generate a unique cache key for the given controlTestId and dataType
   * @param {number|string} controlTestId - The control test identifier
   * @param {string} dataType - One of: 'assignees', 'statuses', 'priorities', 'groups'
   * @returns {string} Cache key
   */
  _getCacheKey(controlTestId, dataType) {
    return `${controlTestId}_${dataType}`;
  }

  /**
   * Retrieve cached data if available
   * @param {number|string} controlTestId - The control test identifier
   * @param {string} dataType - One of: 'assignees', 'statuses', 'priorities', 'groups'
   * @returns {any|null} Cached data or null if not cached
   */
  get(controlTestId, dataType) {
    if (!controlTestId || !dataType) {
      return null;
    }
    const key = this._getCacheKey(controlTestId, dataType);
    // Use has() to distinguish "not cached" from "cached null/empty"
    return this._cache.has(key) ? this._cache.get(key) : null;
  }

  /**
   * Store data in cache
   * @param {number|string} controlTestId - The control test identifier
   * @param {string} dataType - One of: 'assignees', 'statuses', 'priorities', 'groups'
   * @param {any} data - The data to cache
   */
  set(controlTestId, dataType, data) {
    if (!controlTestId || !dataType || data === undefined) {
      return;
    }
    const key = this._getCacheKey(controlTestId, dataType);
    this._cache.set(key, data);
  }

  /**
   * Check if data exists in cache
   * @param {number|string} controlTestId - The control test identifier
   * @param {string} dataType - One of: 'assignees', 'statuses', 'priorities', 'groups'
   * @returns {boolean} True if cached, false otherwise
   */
  has(controlTestId, dataType) {
    if (!controlTestId || !dataType) {
      return false;
    }
    const key = this._getCacheKey(controlTestId, dataType);
    return this._cache.has(key);
  }

  /**
   * Clear cache for a specific controlTestId or all cache if no ID provided
   * Use this when reference data is modified (e.g., new user added, status changed)
   * @param {number|string|null} controlTestId - Optional: clear only this control test's cache
   */
  clear(controlTestId = null) {
    if (controlTestId) {
      CACHE_DATA_TYPES.forEach(dataType => {
        const key = this._getCacheKey(controlTestId, dataType);
        this._cache.delete(key);
      });
    } else {
      this._cache.clear();
    }
  }

  /**
   * Get the current cache size (for debugging/monitoring)
   * @returns {number} Number of cached entries
   */
  size() {
    return this._cache.size;
  }
}

// Export singleton instance
const referenceDataCache = new ReferenceDataCache();
export default referenceDataCache;

// Also export the class for testing purposes
export { ReferenceDataCache };
